import BaseChart from './BaseChart'
import ItemsHelper from '../chart_helpers/ItemsHelper'
import RootHelper from '../chart_helpers/RootHelper'
import AxisHelper, {
  continuousExtent,
  continuousExtentParts,
  cleanCollisions,
} from '../chart_helpers/AxisHelper'
import { categoricalExtent } from '../chart_helpers/AxisHelper'
import SelectableHelper from '../chart_helpers/SelectableHelper'
import * as COLORS from '../utils/colorUtils'
import { interpolateRgb as d3_interpolateRgb } from 'd3-interpolate'
import { select as d3_select } from 'd3-selection'

import { extent as d3_extent } from 'd3-array'
import {
  curveCardinal as d3_curveCardinal,
  curveLinear as d3_curveLinear,
  curveBasis as d3_curveBasis,
  curveMonotoneX as d3_curveMonotoneX,
  line as d3_line,
} from 'd3-shape'
import SizeHelper from '../chart_helpers/SizeHelper'
import { tweenDentedRect } from '../utils/pathUtils'
import { scaleBand as d3_scaleBand } from 'd3-scale'
import {
  drawSvgCircle,
  drawSvgPlus,
  drawSvgPolygon,
  drawSvgSquare,
  drawSvgStar,
  drawSvgTriangle,
} from '../utils/shapeUtils'
import { isDarkMode } from '../utils/configUtils'
import { ACCESSIBILITY_OPTIONS_KEYS } from '../utils/accessibilityUtils'

const MAX_BAR_WIDTH = 40
const MANUAL_BAR_WIDTH_STEPS = 10

function getCurveType(config) {
  if (config.curve === 'smooth') return d3_curveCardinal
  else if (config.curve === 'smooth-b') return d3_curveBasis
  else if (config.curve === 'smooth-m') return d3_curveMonotoneX
  else return d3_curveLinear
}

export default class LineAndBarChartChart extends BaseChart {
  constructor(...args) {
    super(...args)
    this.zeroBased = true
  }
  static defaultConfig = {
    simple: {
      type: Boolean,
      default: true,
    },
    /**
     * {-EN-} Type of curve. `smooth` curves will interpolate between the data points. `shapr` curves will be composed of straight line segments.
     * {-ES-} Tipo de curva. Las curvas `smooth` se interpolarán entre los puntos de datos. Las curvas `shapr`  estarán compuestas por segmentos de línea recta.
     */
    curve: {
      type: String,
      default: 'smooth',
      options: ['smooth', 'sharp' /*'smooth-b'*/, 'smooth-m'],
      exposed: true,
    },
    /**
     * {-EN-} Show/hide the data points that form each line.
     * {-ES-} Muestra/oculta los puntos de datos que forman cada línea.
     */
    showPoints: {
      type: Boolean,
      default: true,
      exposed: true,
    },
    pointsSize: {
      type: [Number, String],
      default: 3,
      options: ['hidden', 3, 6, 9],
      exposed: true,
    },
    /**
     * {-EN-} Show the value on top of each bar.
     * {-ES-} Muestra el valor encima de cada barra.
     */
    showBarDatumValues: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    /**
     * {-EN-} Show the value of each point in the line.
     * {-ES-} Muestra el valor de cada punto de la recta.
     */
    showLineDatumValues: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    highlightNodeOnClick: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    mode: {
      type: String,
      default: 'grouped',
      options: ['single', 'grouped', 'stacked'],
    },
    /**
     * {-EN-} Custom bar width (from 1 to 5)
     * {-ES-} Ancho de barra personalizado (de 1 a 5)
     */
    manualBarWidth: {
      type: [Number, String, null],
      options: Array.from(Array(MANUAL_BAR_WIDTH_STEPS))
        .map((_, i) => [i + 1, (i + 1).toString()])
        .flat(),
      default: null,
      exposed: true,
    },
    ItemsHelper: {
      colorScale: {
        // me lo he llevado al "viz" (??)
        // value: ['categorical', 'categorical'],
        // options:[['categorical','single'],['categorical','single']],
        // multiValueIds: ['lines', 'bars'],
      },
      colorMode: {
        value: ['stroke', 'fill'],
        multiValueIds: ['lines', 'bars'],
      },
      colorDict: {
        value: [null, null],
        multiValueIds: ['lines', 'bars'],
      },
    },
    AxisHelper: {
      zoom: {
        nonEditable: true,
      },
      scroll: {
        default: false,
        exposed: true,
      },
      showScrollbar: {
        default: true,
        exposed: true,
      },
      x: {
        scale: 'band',
        getExtent: function (data) {
          return categoricalExtent(data.bars.length ? data.bars : data.lines[0].value) //data.bars.map((d)=>d.id)
        },
      },
      y: {
        getExtent: function (data, dimLabel, dimId) {
          // "TIENEN QUE MATCHEAR LAS "SCALES""....
          let extent, extentParts
          if (dimId === 'y_0') {
            extent = d3_extent(
              data.lines.reduce((res, d) => res.concat(d3_extent(d.value, dd => dd.value)), [])
            )
            extentParts = continuousExtentParts(data.lines)
            // console.log("line extent bef",extent)
            if (this.config.AxisHelper.y.showTickValues && this.config.AxisHelper.y.unit) {
              extent = cleanCollisions(this.axes.y_0, extent, extentParts)
              // NOSE COMO adaptar el cleancollisions para que mire en el "otro axis"...
              extent = cleanCollisions(this.axes.y_1, extent, extentParts)
              // console.log("line extent aft",extent)
            }
          } else if (dimId === 'y_1') {
            // extentParts = data.bars.map(d => ({ value: [d, d] }))
            // extent = continuousExtent(data.bars)
            // // console.log("bar extent bef",extent)
            // if (this.config.AxisHelper.y.showTickValues && this.config.AxisHelper.y.unit) {
            //   extent = cleanCollisions(this.axes.y_1, extent, extentParts)
            //   // NOSE COMO adaptar el cleancollisions para que mire en el "otro axis"...
            //   extent = cleanCollisions(this.axes.y_0, extent, extentParts)
            //   // console.log("bar extent aft",extent)
            // }
            if (this.config.mode === 'stacked') {
              extentParts = continuousExtentParts(data.bars, d => [d.y0, d.y1])
              // console.log("extent parts",extentParts)
              extent = continuousExtent(extentParts)
            } else if (this.config.mode === 'grouped') {
              extentParts = continuousExtentParts(data.bars)
              // console.log("extent parts",extentParts)
              extent = continuousExtent(extentParts) //d3.extent(data.reduce((res,d)=>res.concat(d3.extent(d.value,(dd)=>dd.value)),[]))
            } else {
              extentParts = data.bars.map(d => ({ value: [d.value, d.value] })) //ESTABA MAL ESTOª!!!!! no?
              // console.log("extent parts",extentParts)
              extent = continuousExtent(data.bars) //d3.extent(data,(d)=>d.value)
            }

            if (this.config.AxisHelper.y.showTickValues && this.config.AxisHelper.y.unit) {
              extent = cleanCollisions(this.axes.y_1, extent, extentParts)
            }
          }
          return extent
        },
        dimensions: ['lines', 'bars'],
        axisLabel: {
          value: ['lines dim', 'bars dim'],
          multiValueIds: ['lines', 'bars'],
        },
        showAxisLabel: false,
        valueFormat: {
          value: ['[N]0.0', '[N]0.0'],
          multiValueIds: ['lines', 'bars'],
        },
        unit: {
          value: ['line units', 'bar units'],
          multiValueIds: ['lines', 'bars'],
        },
        tickOrientation: {
          value: ['horizontal', 'horizontal'],
          multiValueIds: ['lines', 'bars'],
        },
      },
    },
    TooltipBehavior: {
      itemsKeys: ['linesItems', 'barsItems'],
      getItemOffset: function ($el) {
        let original = $el.getBoundingClientRect()
        return {
          x: original.x + original.width / 2,
          y: original.top, //- 5
        }
      },
      getHtml: function (d, itemsKey) {
        console.log('should use onAxis', itemsKey)
        let onAxis = itemsKey === 'linesItems' ? 'y_0' : 'y_1'
        if (itemsKey === 'linesItems') {
          return `
            <div class="tip-value important">${this.formatTooltipValue(d.value, onAxis)}</div>
            <div class="tip-id">
              <span class="icon" style="display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: ${this.color(
                { id: d.parentId },
                0
              )}; margin-right: 3px"></span>
              ${d.parentId}
            </div>
            <div class="secondary">${this.formatValue(d.id, 'x')}</div>
            `
        } else {
          if (this.cachedColorScale[1].isSinlge || this.config.simple)
            return `
            <div class="tip-value important">${this.formatTooltipValue(d.value, onAxis)}</div>
            <div class="tip-id">
              <span class="icon" style="display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: ${this.color(
                { id: d.parentId },
                1
              )}; margin-right: 3px"></span>
              ${d.parentId}
            </div>
            <div class="secondary">${this.formatValue(d.id, 'x')}</div>`
          else
            return `
            <div class="tip-value important">${this.formatTooltipValue(d.value, onAxis)}</div>
            <div class="tip-id">
              <span class="icon" style="display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: ${this.color(
                { id: d.id },
                1
              )}; margin-right: 3px"></span>
              ${d.id}
            </div>
              `
        }
      },
    },
    highlightMaxValue: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    highlightMinValue: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    highlightTotalMinMaxValue: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    /*
     * {-EN-} Highlight an specific point by id
     * {-ES-} Resalta un punto específico por id.
     */
    highlightedPoint: {
      type: [String, Number, null],
      default: null,
      exposed: true,
    },
    highlightedPointSize: {
      type: Number,
      default: 8,
      exposed: true,
    },
    highlightedPointStroke: {
      type: Number,
      default: 3,
      exposed: true,
    },
    highlightedNodeLines: {
      type: [String, Number],
      default: 'all',
      exposed: true,
    },
    hasColorPatterns: {
      type: Boolean,
      default: true,
      exposed: false,
    },
  }
  static helpers = [RootHelper, ItemsHelper, SelectableHelper, SizeHelper, AxisHelper]
  checkConsistency() {
    return true
  }
  draw() {
    function fillTween(d, index) {
      const fill = self.fill(d, index)
      if (fill?.includes('url'))
        return function (t) {
          return fill
        }
      else {
        const currentFill = d3_select(this).attr('fill')
        var i = d3_interpolateRgb(currentFill, fill)
        return function (t) {
          return i(t)
        }
      }
    }
    let minMaxValues = {}
    if (this.config.highlightMaxValue || this.config.highlightMinValue)
      if (this.config.highlightTotalMinMaxValue) {
        ;[...this.data.lines, ...this.data.bars].forEach(itemData => {
          itemData.value.forEach(item => {
            const value = this.config.mode === 'stacked' && !!item.barMax ? item.barMax : item.value
            if (!minMaxValues.min || !minMaxValues.max)
              minMaxValues = {
                min: { id: item.id, value: value },
                max: { id: item.id, value: value },
              }
            else {
              if (minMaxValues.max.value < value) {
                minMaxValues.max.value = value
                minMaxValues.max.parentId = item.parentId
                minMaxValues.max.id = item.id
              } else if (minMaxValues.min.value > value) {
                minMaxValues.min.value = value
                minMaxValues.min.parentId = item.parentId
                minMaxValues.min.id = item.id
              }
            }
          })
        })
      } else {
        ;[...this.data.lines, ...this.data.bars].forEach(itemData => {
          itemData.value.forEach(item => {
            //Take in account every line item and in case of stacked burs just last layer bars
            if (
              this.config.mode !== 'stacked' ||
              (this.config.mode === 'stacked' && !item.barMax) ||
              item.isLastLayer
            ) {
              const value =
                this.config.mode === 'stacked' && !!item.barMax ? item.barMax : item.value

              if (!minMaxValues[item.parentId])
                minMaxValues[item.parentId] = {
                  min: { id: item.id, value: value },
                  max: { id: item.id, value: value },
                }
              else {
                if (minMaxValues[item.parentId].max.value < value) {
                  minMaxValues[item.parentId].max.value = value
                  minMaxValues[item.parentId].max.id = item.id
                } else if (minMaxValues[item.parentId].min.value > value) {
                  minMaxValues[item.parentId].min.value = value
                  minMaxValues[item.parentId].min.id = item.id
                }
              }
            }
          })
        })
      }

    const getLimitDots = (parentId, id) => {
      const result = []
      const minMaxValuesComputed = this.config.highlightTotalMinMaxValue
        ? minMaxValues
        : minMaxValues[parentId]
      if (this.config.highlightMaxValue) {
        if (
          minMaxValuesComputed?.max &&
          minMaxValuesComputed.max.id === id &&
          (!minMaxValuesComputed.max.parentId || minMaxValuesComputed.max.parentId === parentId)
        ) {
          result.push({ parentId: parentId, value: minMaxValuesComputed?.max })
        }
      }
      if (this.config.highlightMinValue) {
        if (
          minMaxValuesComputed?.min &&
          minMaxValuesComputed.min.id === id &&
          (!minMaxValuesComputed.min.parentId || minMaxValuesComputed.min.parentId === parentId)
        ) {
          result.push({ parentId: parentId, value: minMaxValuesComputed?.min })
        }
      }
      return result
    }

    // UN PCO BULLSHIT TODO ESTO DEL LONG SHORT... casi mejor lo limpiamos...
    let longDir = 'width'
    // let shortDir = 'height'
    let getTranslate = (long, short) => {
      return `translate(${long},${short})`
    }

    let linesScale = this.axes.y_0.func.scale()
    let barsScale = this.axes.y_1.func.scale()
    let xScale = this.axes.x.func.scale()

    let offsetValue
    if (barsScale.domain()[0] < 0 && barsScale.domain()[1] > 0) offsetValue = 0
    else offsetValue = barsScale.domain()[0]

    let getOffset = v => Math.min(v, barsScale(offsetValue))
    let getShort = v => Math.abs(barsScale(offsetValue) - v)

    let barPercent = 0.7
    // let barWidth = xScale.bandwidth()*barPercent
    // let barMargin = xScale.bandwidth()*(1-barPercent)/2
    let barWidth = Math.min(MAX_BAR_WIDTH, xScale.bandwidth() * barPercent)
    this.config.manualBarWidth !== null &&
      (barWidth = (this.config.manualBarWidth / (MANUAL_BAR_WIDTH_STEPS / 2)) * barWidth)
    let barMargin = (xScale.bandwidth() - barWidth) / 2
    let subBarWidth
    // this.barsItemsEnter.append('rect')
    // let drawItem = (sel,status)=>{
    //   sel.attr('transform', d => {
    //     if(status==='enter') return getTranslate(xScale(d.id) + barMargin, getOffset(barsScale(0)))
    //     else return getTranslate(xScale(d.id) + barMargin, getOffset(barsScale(d.value)))
    //   })
    //   sel.select('path')
    //     .attr('fill', d => this.color(d, 1))
    //     // .attr('d', d => {
    //   sel.select('path').attrTween && sel.select('path').attrTween('d',function(d){
    //     // let w = longDir === 'width' ? barWidth : getShort(barsScale(d.value))
    //     // let h = longDir === 'width' ? getShort(barsScale(d.value)) : barWidth
    //     // let dent =
    //     //   longDir === 'width'
    //     //     ? d.value > 0
    //     //       ? ['tl', 'br']
    //     //       : 'bl'
    //     //     : d.value > 0
    //     //     ? ['bl', 'tr']
    //     //     : 'tr'
    //     // return dentedRect(w, h, dent)
    //     let barLength
    //     if (status === 'enter' || status === 'exit') barLength = 0
    //     else barLength = getShort(barsScale(d.value))
    //     return tweenDentedRect.call(this,d,{
    //       barLength,
    //       barWidth,
    //       longDir,
    //       scale: barsScale
    //     })
    //   })
    // }
    let drawItem
    if (this.config.mode === 'stacked') {
      let stackPadding = 2
      drawItem = (sel, status) => {
        sel.attr('transform', d => {
          let barPos
          if (status === 'enter' || status === 'exit') barPos = barsScale(0)
          else {
            //Condition in order to prevent overlapping when setting manual domain
            if (this.config.horizontal && !d.isFirstLayer && d.y0 < barsScale.domain()[0]) {
              barPos = 0
            } else {
              barPos = barsScale(this.config.horizontal ? d.y0 : d.y1)
            }
          }
          return getTranslate(
            xScale(d.id) + barMargin,
            barPos +
              (this.config.horizontal
                ? d.isFirstLayer && barsScale.domain()[0] > 0
                  ? Math.abs(barsScale(0))
                  : 0
                : stackPadding / 2)
          )
        })
        /*
        sel
          .select('path')
          .attr('fill', d =>
            this.color({ id: this.allData.bars.dimensions ? d.parentId : d.id }, 1)
          )
        */
        if (sel.select('path').attrTween) {
          sel.select('path').attrTween('d', function (d) {
            let barLength
            if (status === 'enter' || status === 'exit') barLength = 0
            else {
              let [y0, y1] = [d.y0, d.y1]
              //Condition block in order to prevent overlapping when setting manual domain
              //Check if is negative domain
              if (barsScale.domain()[0] < 0)
                barLength =
                  getShort(barsScale(d.safeValue)) - (d.isFirstLayer ? stackPadding : stackPadding)
              //Check if domain is higher than the data and if is not first layer
              else if (!d.isFirstLayer && y0 < barsScale.domain()[0]) {
                barLength = getShort(barsScale(y1)) - stackPadding
              } else {
                self.config.horizontal
                  ? (barLength =
                      getShort(
                        barsScale(y1) -
                          barsScale(y0) -
                          (d.isFirstLayer ? Math.abs(barsScale(0)) : 0)
                      ) - stackPadding)
                  : (barLength =
                      getShort(
                        barsScale(y1) -
                          barsScale(y0) +
                          (!d.isFirstLayer ? barsScale.range()[0] : barsScale(0))
                      ) - stackPadding)
              }
            }
            barLength = Math.max(barLength, 0) //y le faltaba esto para cuando el "safeValue" da 0!!!

            return tweenDentedRect.call(this, d, {
              barLength,
              barWidth,
              longDir,
              scale: barsScale,
            })
          })
          sel.select('path').attrTween('fill', function (d) {
            return fillTween.bind(this)({ id: self.allData.bars.dimensions ? d.parentId : d.id }, 1)
          })
        }
      }
    } else if (this.config.mode === 'grouped') {
      barWidth = this.axes.x.scale.bandwidth() * barPercent
      this.config.manualBarWidth !== null &&
        (barWidth = (this.config.manualBarWidth / (MANUAL_BAR_WIDTH_STEPS / 2)) * barWidth)
      barMargin = (this.axes.x.scale.bandwidth() - barWidth) / 2
      const barIds = this.data.bars.map(d => d.id)
      let subScale = d3_scaleBand()
        .domain(barIds)
        .range([barMargin, barMargin + barWidth]) //.range([w/2-w/6,w/2+w/3-w/6])
      let subBarPercent = 0.7
      // let subBarWidth = subScale.bandwidth()*subBarPercent
      // let subBarMargin = subScale.bandwidth()*(1-subBarPercent)/2
      subBarWidth = Math.min(MAX_BAR_WIDTH, subScale.bandwidth() * subBarPercent)
      //Specific subBarWidth if there is only one group in order to avoid overlapping with multiple groups
      this.config.manualBarWidth !== null &&
        barIds.length === 1 &&
        (subBarWidth = (this.config.manualBarWidth / (MANUAL_BAR_WIDTH_STEPS / 2)) * subBarWidth)
      let subBarMargin = (subScale.bandwidth() - subBarWidth) / 2
      drawItem = (sel, status) => {
        sel.attr('transform', d => {
          let barPos
          if (status === 'enter' || status === 'exit') barPos = getOffset(barsScale(0))
          else barPos = getOffset(barsScale(d.safeValue))
          return getTranslate(xScale(d.id) + subScale(d.parentId) + subBarMargin, barPos)
        })
        /*
        sel
          .select('path')
          .attr('fill', d =>
            this.color({ id: this.allData.bars.dimensions ? d.parentId : d.id }, 1)
          )
          */
        // .attr('d', d => {
        if (sel.select('path').attrTween) {
          sel.select('path').attrTween('d', function (d) {
            let barLength
            if (status === 'enter' || status === 'exit') barLength = 0
            else barLength = getShort(barsScale(d.safeValue))
            // let w = longDir === 'width' ? subBarWidth : barLength
            // let h = longDir === 'width' ? barLength : subBarWidth
            // let dent =
            //   longDir === 'width'
            //     ? d.safeValue > 0
            //       ? ['tl', 'br']
            //       : 'bl'
            //     : d.safeValue > 0
            //     ? ['bl', 'tr']
            //     : 'tr'

            let gap = null
            if (barsScale.isChunky && d.safeValue > barsScale._threshold) {
              gap = barsScale(barsScale._threshold) - barsScale(d.safeValue)
            }

            // return dentedRect(w, h, dent, gap)
            //Por que estos datos son stacked??? Quitamos los y0 e y1 para que tweenDetedRect haga los arcos inferiores @oslo ??
            // const { y0, y1, ...dUnstacked } = d
            return tweenDentedRect.call(this, d, {
              barLength,
              barWidth: subBarWidth,
              longDir,
              scale: barsScale,
              gapStart: gap,
            })
          })
          sel.select('path').attrTween('fill', function (d) {
            return fillTween.bind(this)({ id: self.allData.bars.dimensions ? d.parentId : d.id }, 1)
          })
        }
      }
    } else {
      drawItem = (sel, status) => {
        sel.attr('transform', d => {
          let barPos
          if (status === 'enter' || status === 'exit') barPos = getOffset(barsScale(0))
          else barPos = getOffset(barsScale(d.safeValue))
          return getTranslate(xScale(d.id) + barMargin, barPos)
        })
        //sel.select('path').attr('fill', d => this.color({ id: d.id }, 1))
        // .attr('d', d => {
        if (sel.select('path').attrTween) {
          sel.select('path').attrTween('d', function (d) {
            let barLength
            if (status === 'enter' || status === 'exit') barLength = 0
            else barLength = getShort(barsScale(d.safeValue))
            // let w = longDir === 'width' ? barWidth : barLength
            // let h = longDir === 'width' ? barLength : barWidth
            // let dent =
            //   longDir === 'width'
            //     ? d.safeValue > 0
            //       ? ['tl', 'br']
            //       : 'bl'
            //     : d.safeValue > 0
            //     ? ['bl', 'tr']
            //     : 'tr'

            let gap = null
            if (barsScale.isChunky && d.safeValue > barsScale._threshold) {
              gap = barsScale(barsScale._threshold) - barsScale(d.safeValue)
            }
            return tweenDentedRect.call(this, d, {
              barLength,
              barWidth,
              longDir,
              scale: barsScale,
              gapStart: gap,
            })
            // return dentedRect(w, h, dent, gap)
          })
          sel.select('path').attrTween('fill', function (d) {
            return fillTween.bind(this)({ id: d.id }, 1)
          })
          //sel.select('path').attr('fill', d => this.color({ id: d.id }, 1))
        }
      }
    }
    this.barsItemsEnter.append('path')
    this.barsItemsTrans.call(drawItem)
    this.barsItemsEnter.call(drawItem, 'enter')
    this.barsItemsEnterTrans.call(drawItem)
    // this.barsItemsEnter
    //   .attr('transform', d => getTranslate(xScale(d.id) + barMargin, getOffset(barsScale(0))))
    //   .select('path')
    //   .attr('fill', d => this.color(d, 1))
    //   .attr('d', d => {
    //     let w = longDir === 'width' ? barWidth : 0
    //     let h = longDir === 'width' ? 0 : barWidth
    //     let dent =
    //       longDir === 'width'
    //         ? d.value > 0
    //           ? ['tl', 'br']
    //           : 'bl'
    //         : d.value > 0
    //         ? ['bl', 'tr']
    //         : 'tr'
    //     return dentedRect(w, h, dent)
    //   })
    // this.barsItemsEnterTrans
    //   .attr('transform', d => getTranslate(xScale(d.id) + barMargin, getOffset(barsScale(d.value))))
    //   .select('path')

    //   .attr('d', d => {
    //     let w = longDir === 'width' ? barWidth : getShort(barsScale(d.value))
    //     let h = longDir === 'width' ? getShort(barsScale(d.value)) : barWidth
    //     let dent =
    //       longDir === 'width'
    //         ? d.value > 0
    //           ? ['tl', 'br']
    //           : 'bl'
    //         : d.value > 0
    //         ? ['bl', 'tr']
    //         : 'tr'
    //     return dentedRect(w, h, dent)
    //   })
    // this.barsItems.lower()
    // this.barsItemsTrans
    //   .attr('transform', d => getTranslate(xScale(d.id) + barMargin, getOffset(barsScale(d.value))))
    //   .select('path')
    //   .attr('fill', d => this.color(d, 1))
    //   .attr('d', d => {
    //     let w = longDir === 'width' ? barWidth : getShort(barsScale(d.value))
    //     let h = longDir === 'width' ? getShort(barsScale(d.value)) : barWidth
    //     let dent =
    //       longDir === 'width'
    //         ? d.value > 0
    //           ? ['tl', 'br']
    //           : 'bl'
    //         : d.value > 0
    //         ? ['bl', 'tr']
    //         : 'tr'
    //     return dentedRect(w, h, dent)
    //   })

    this.barsItemsExitTrans.style('opacity', 0)
    this.barsParentItemsExitTrans && this.barsParentItemsExitTrans.style('opacity', 0)

    const barsItemsMerged = this.barsItemsEnter.merge(this.barsItems)

    let barLimitDotsGhost = barsItemsMerged.selectAll('.limit-dot-ghost').data(
      d => {
        return getLimitDots(d.parentId, d.id)
      },
      (_, i) => i
    )
    const barLimitDotsGhostEnter = barLimitDotsGhost
      .enter()
      .append('circle')
      .attr('class', 'limit-dot-ghost')
      .attr('r', 0)
      .attr('opacity', 0)
      .attr('stroke-width', 2.5)
    //.style('stroke-dasharray', 2)
    barLimitDotsGhost.exit().transition().duration(800).attr('r', 0).attr('opacity', 0).remove()
    barLimitDotsGhost = barLimitDotsGhost.merge(barLimitDotsGhostEnter)

    let barLimitDots = barsItemsMerged.selectAll('.limit-dot').data(
      d => {
        return getLimitDots(d.parentId, d.id)
      },
      (_, i) => i
    )
    const barLimitDotsEnter = barLimitDots
      .enter()
      .append('circle')
      .attr('class', 'limit-dot')
      .attr('r', 0)
      .attr('opacity', 0)
    //.style('stroke-dasharray', 2)
    barLimitDots.exit().transition().duration(800).attr('r', 0).attr('opacity', 0).remove()
    barLimitDots = barLimitDots.merge(barLimitDotsEnter)

    const getLimitDotsXPosition = d => {
      if (this.config.horizontal) {
        return getShort(this.axes.y.func.scale()(d.safeValue)) //+ 12
      } else {
        return this.config.mode === 'grouped' ? subBarWidth / 2 : barWidth / 2
      }
    }

    const getLimitDotsYPosition = d => {
      if (this.config.horizontal) {
        return this.config.mode === 'grouped' ? subBarWidth / 2 : barWidth / 2
      } else {
        //return -10
        return 0
      }
    }

    barLimitDotsGhost
      .raise()
      .attr('cx', d => getLimitDotsXPosition(d))
      .attr('cy', d => getLimitDotsYPosition(d))
      .attr('fill', 'transparent')
      .attr(
        'stroke',
        !isDarkMode(this.config, this._parent.accessibilityOptions)
          ? COLORS.SKIN_BACKGROUND
          : COLORS.SKIN_BACKGROUND_DARK
      )
      .transition()
      .duration(800)
      .attr('r', this.config.mode === 'grouped' ? (subBarWidth / 2) * 0.95 : (barWidth / 2) * 0.95)
      .attr('opacity', 1)

    barLimitDots
      .raise()
      .attr('cx', d => getLimitDotsXPosition(d))
      .attr('cy', d => getLimitDotsYPosition(d))
      .attr('fill', 'transparent')
      .attr(
        'stroke',
        isDarkMode(this.config, this._parent.accessibilityOptions) ? '#FFFFFF' : '#000000'
      )
      .transition()
      .duration(800)
      .attr('r', this.config.mode === 'grouped' ? (subBarWidth / 2) * 0.95 : (barWidth / 2) * 0.95)
      .attr('opacity', 1)

    // BAR DATUMS LABELS
    let datumValues = this.barsItemsEnter
      .merge(this.barsItems)
      .selectAll('.datum-value')
      // .data(d => (this.config.showBarDatumValues ? [d] : []))
      .data(d => {
        if (this.config.showBarDatumValues) {
          if (this.config.mode === 'stacked') {
            if (this.config.datumValuesPosition === 'center') {
              return [{ value: d.y1, safeValue: d.y1 - d.y0 }]
            } else if (this.config.datumValuesPosition === 'bottom') {
              if (d.isFirstLayer) return [{ ...d, value: d.barMax }]
              return []
            } else {
              if (d.isLastLayer) return [{ ...d, value: d.barMax }]
              return []
            }
          } else {
            return [d]
          }
        } else {
          return []
        }
      })
    datumValues.exit().transition().style('opacity', 0).remove()

    let datumValuesEnter = datumValues
      .enter()
      .append('g')
      .attr('class', 'datum-value')
      .style('opacity', 0)

    datumValuesEnter.append('rect')

    datumValuesEnter.append('text').style('font-weight', 'bold').attr('fill', 'currentColor')

    datumValues = datumValues.merge(datumValuesEnter)

    datumValues
      .attr(
        'font-size',
        this._parent.accessibilityOptions?.[ACCESSIBILITY_OPTIONS_KEYS.increaseTextSize]
          ? '18px'
          : '14px'
      )
      .transition()
      .style('opacity', 1)

    datumValues.select('text').text(d => this.formatValue(d.value, 'y_1'))

    datumValues
      .select('text')
      .attr('text-anchor', 'middle')
      .attr('x', this.config.mode === 'grouped' ? subBarWidth / 2 : barWidth / 2)
      .attr('y', -8)

    // NO ESTABA BIEN, si hace falta hay que hacerlo mejor...
    // if (this.config.showBarDatumValues) {
    //   let bbox = datumValues.select('text').node().getBBox()
    //   datumValues
    //     .select('rect')
    //     .attr(
    //       'fill',
    //       isDarkMode(this.config, this._parent.accessibilityOptions)? 'rgba(31, 31, 31, 0.8)' : 'rgba(255,255,255,0.8)'
    //     )
    //     .attr('width', bbox.width + 4)
    //     .attr('height', bbox.height)
    //     .attr('x', barWidth / 2 - bbox.width / 2 - 2)
    //     .attr('y', -8 - bbox.height / 2 - 4)
    //     .attr('rx', 2)
    // }

    ///////////////LINES//////////

    let line = d3_line()
      .curve(getCurveType(this.config))
      // .curve(this.config.curve === 'smooth' ? d3_curveCardinal : d3_curveLinear)
      .x(d => xScale(d.id) + barMargin + barWidth / 2)
      .y(d => linesScale(d.value))

    let self = this
    this.linesParentItemsEnter
      .append('path')
      .attr('class', 'ghost-path')
      .attr('fill', 'none')
      .attr('stroke-width', d => this.thickness(d, 0) + 1)
      .attr(
        'stroke',
        !isDarkMode(this.config, this._parent.accessibilityOptions)
          ? COLORS.SKIN_BACKGROUND
          : COLORS.SKIN_BACKGROUND_DARK
      )
      .attr('d', d => line(d.value))
      .style('opacity', 0)

    this.linesParentItemsEnter
      .append('path')
      .attr('class', 'real-path')
      .attr('fill', 'none')
      .attr('stroke', d => this.color(d, 0))
      .attr('stroke-width', d => this.thickness(d, 0))
      .attr('d', d => line(d.value))
      .each(function () {
        this._totalLength = this.getTotalLength()
      })
      .attr('stroke-dasharray', function (d) {
        var dashing = self.strokeDasharray(d)
        if (dashing === 'none') dashing = '1 0'
        var dashLength = dashing
          .split(/[\s,]/)
          .map(function (a) {
            return parseFloat(a) || 0
          })
          .reduce(function (a, b) {
            return a + b
          })
        var dashCount = Math.ceil(this._totalLength / dashLength)
        var newDashes = new Array(dashCount).join(dashing + ' ')
        var dashArray = newDashes + ' 0, ' + this._totalLength
        return dashArray
      })
      .attr('stroke-dashoffset', function () {
        return this._totalLength
      })

    this.linesParentItemsEnterTrans.select('.ghost-path').style('opacity', 1)

    this.linesParentItemsEnterTrans.select('.real-path').attr('stroke-dashoffset', 0)

    this.linesParentItems.select('.real-path').attr('stroke-dasharray', 'none')

    this.linesParentItemsTrans
      .select('.real-path')
      .attr('stroke-dasharray', d => this.strokeDasharray(d, 0))
      .attr('stroke-width', d => this.thickness(d, 0))
    this.linesParentItemsTrans
      .select('.ghost-path')
      .attr('stroke-width', d => this.thickness(d, 0) + 1)

    this.linesParentItemsTrans.select('.real-path').attr('stroke', d => this.color(d, 0))

    this.linesParentItemsTrans.select('.real-path').attr('d', d => line(d.value))
    this.linesParentItemsTrans
      .select('.ghost-path')
      .attr('d', d => line(d.value))
      .attr(
        'stroke',
        !isDarkMode(this.config, this._parent.accessibilityOptions)
          ? COLORS.SKIN_BACKGROUND
          : COLORS.SKIN_BACKGROUND_DARK
      )

    this.linesParentItemsExitTrans.style('opacity', 0)

    this.linesParentItems.merge(this.linesParentItemsEnter).raise()

    // NO ESTA BIEN AUN...
    let linesItems = this.linesItems.merge(this.linesItemsEnter)
    let breakpoints = linesItems
      .raise()
      .selectAll('.break-point')
      .data(d => (this.config.showPoints && (d.value || d.value == 0) ? [d] : []))
    breakpoints.exit().transition().style('opacity', 0).remove()
    let breakpointsEnter = breakpoints.enter().append('g').attr('class', 'break-point')

    //dots to enlarge click area
    //breakpointsEnter.append('circle').attr('r', '20').attr('opacity', 0)

    //breakpointsEnter.append('circle').attr('class', 'dot')
    breakpointsEnter.attr(
      'transform',
      d => `translate(${xScale(d.id) + barMargin + barWidth / 2},${linesScale(d.value)})`
    )

    breakpoints = breakpoints.merge(breakpointsEnter)
    const getPointType = d => {
      if (!this.config.ItemsHelper.colorDict[0]?.[d.parentId]?.shape) return 'circle'
      return this.config.ItemsHelper.colorDict[0]?.[d.parentId].shape
    }

    const isDotHighlighted = d => {
      const interactivityHighlightedNode =
        this.config.highlightNodeOnClick &&
        this._interactiveHighlightedNodes?.[d?.parentId]?.toString() === d?.id.toString()
      return (
        interactivityHighlightedNode ||
        (!this.config.highlightNodeOnClick &&
          (this.config.highlightedNodeLines === 'all' ||
            this.config.highlightedNodeLines === d.parentId) &&
          this.config.highlightedPoint &&
          d?.id.toString() === this.config.highlightedPoint.toString())
      )
    }

    const drawShapes = (className, shapeType, drawEnterCb, drawMergeCb) => {
      let shapes = breakpoints.selectAll(`.${className}`).data(
        d =>
          (isDotHighlighted(d) && getPointType(d) === shapeType) ||
          (this.config.showPoints &&
            this.config.pointsSize !== 0 &&
            this.config.pointsSize !== 'hidden' &&
            getPointType(d) === shapeType)
            ? [d]
            : [],
        d => d.id
      )

      const shapesEnter = shapes
        .enter()
        .append('g')
        .attr('class', `point-shape ${className}`)
        .attr('stroke-width', 0)
      shapesEnter.call(drawEnterCb)
      shapes.exit().transition().duration(800).style('opacity', 0).remove()
      shapes = shapes.merge(shapesEnter)
      shapes
        .attr(
          'fill',
          !isDarkMode(this.config, this._parent.accessibilityOptions)
            ? COLORS.SKIN_BACKGROUND
            : COLORS.SKIN_BACKGROUND_DARK
        )
        .attr('stroke', d => this.color({ id: d.parentId }, 0))
        .attr('stroke-width', d => (isDotHighlighted(d) ? this.config.highlightedPointStroke : 1))

      this.linesItemsEnterTrans.select('.break-point').select(`.${className}`).call(drawMergeCb)

      this.linesItemsTrans
        .select('.break-point')
        .attr(
          'transform',
          d => `translate(${xScale(d.id) + barMargin + barWidth / 2},${linesScale(d.value)})`
        )
        .select(`.${className}`)
        .attr(
          'fill',
          !isDarkMode(this.config, this._parent.accessibilityOptions)
            ? COLORS.SKIN_BACKGROUND
            : COLORS.SKIN_BACKGROUND_DARK
        )
        .attr('stroke', d => this.color({ id: d.parentId }, 0))
        .attr('stroke-width', d => (isDotHighlighted(d) ? this.config.highlightedPointStroke : 1))
        .call(drawMergeCb)

      this.linesItemsExitTrans.select(`.${className}`).attr('opacity', 0)
    }
    const SHAPES_DEFAULT_SIZE = this.config.pointsSize
      ? this.config.pointsSize === 'hidden'
        ? 0
        : this.config.pointsSize
      : 3
    //Circle
    drawShapes(
      'circle',
      'circle',
      sel => drawSvgCircle(sel, 0, 'enter'),
      sel =>
        drawSvgCircle(
          sel,
          d => (isDotHighlighted(d) ? this.config.highlightedPointSize : SHAPES_DEFAULT_SIZE),
          'edit'
        )
    )

    //Square
    drawShapes(
      'square',
      'square',
      sel => drawSvgSquare(sel, 0, 'enter'),
      sel =>
        drawSvgSquare(
          sel,
          d =>
            isDotHighlighted(d) ? this.config.highlightedPointSize * 2 : SHAPES_DEFAULT_SIZE * 2,
          'edit'
        )
    )
    //Triangles
    drawShapes(
      'triangle',
      'triangle',
      sel => drawSvgTriangle(sel, 0, 'enter'),
      sel =>
        drawSvgTriangle(
          sel,
          d =>
            isDotHighlighted(d) ? this.config.highlightedPointSize * 2 : SHAPES_DEFAULT_SIZE * 2,
          'edit'
        )
    )

    //Five point star
    drawShapes(
      'five-point-star',
      'fivePointStar',
      sel => drawSvgStar(sel, 0, 5, 'enter'),
      sel =>
        drawSvgStar(
          sel,
          d =>
            isDotHighlighted(d) ? this.config.highlightedPointSize * 2 : SHAPES_DEFAULT_SIZE * 2,
          5,
          'edit'
        )
    )

    //Six point star
    drawShapes(
      'six-point-star',
      'sixPointStar',
      sel => drawSvgStar(sel, 0, 6, 'enter'),
      sel =>
        drawSvgStar(
          sel,
          d =>
            isDotHighlighted(d) ? this.config.highlightedPointSize * 2 : SHAPES_DEFAULT_SIZE * 2,
          6,
          'edit'
        )
    )

    //Hexagon
    drawShapes(
      'hexagon',
      'hexagon',
      sel => drawSvgPolygon(sel, 0, 6, 'enter'),
      sel =>
        drawSvgPolygon(
          sel,
          d =>
            isDotHighlighted(d) ? this.config.highlightedPointSize * 2 : SHAPES_DEFAULT_SIZE * 2,
          6,
          'edit'
        )
    )

    //Pentagon
    drawShapes(
      'pentagon',
      'pentagon',
      sel => drawSvgPolygon(sel, 0, 5, 'enter'),
      sel => drawSvgPolygon(sel, SHAPES_DEFAULT_SIZE * 2, 5, 'edit')
    )

    //Plus
    drawShapes(
      'plus',
      'plus',
      sel => drawSvgPlus(sel, 0, 'enter'),
      sel => drawSvgPlus(sel, SHAPES_DEFAULT_SIZE * 2, 'edit')
    )

    /*
    this.linesItemsEnter
      .select('.break-point')
      .attr(
        'transform',
        d => `translate(${xScale(d.id) + barMargin + barWidth / 2},${linesScale(d.value)})`
      )
      .select('.dot')
      .attr(
        'fill',
        !isDarkMode(this.config, this._parent.accessibilityOptions) ? COLORS.SKIN_BACKGROUND : COLORS.SKIN_BACKGROUND_DARK
      )
      .attr('stroke', d => this.color({ id: d.parentId }, 0))
      .attr('r', 0)
    this.linesItemsEnterTrans.select('.break-point').select('.dot').attr('r', 3)
    this.linesItemsTrans
      .select('.break-point')
      .attr(
        'transform',
        d => `translate(${xScale(d.id) + barMargin + barWidth / 2},${linesScale(d.value)})`
      )
      .select('.dot')
      .attr(
        'fill',
        !isDarkMode(this.config, this._parent.accessibilityOptions) ? COLORS.SKIN_BACKGROUND : COLORS.SKIN_BACKGROUND_DARK
      )
      .attr('stroke', d => this.color({ id: d.parentId }, 0))
      .attr('r', 3)
    this.linesItemsExitTrans.select('.dot').attr('opacity', 0)
    */

    //Line min and max dots
    let limitDotsGhost = breakpoints
      .merge(breakpointsEnter)
      .selectAll('.limit-dot-ghost')
      .data(
        d => getLimitDots(d.parentId, d.id),
        (_, i) => i
      )
    const limitDotsGhostEnter = limitDotsGhost
      .enter()
      .append('circle')
      .attr('class', 'limit-dot-ghost')
      .attr('r', 12)
      .attr('stroke-width', 2.5)
      .attr(
        'stroke',
        !isDarkMode(this.config, this._parent.accessibilityOptions)
          ? COLORS.SKIN_BACKGROUND
          : COLORS.SKIN_BACKGROUND_DARK
      )
    //.style('stroke-dasharray', 2)
    limitDotsGhost.exit().transition().duration(800).attr('r', 0).attr('opacity', 0).remove()
    limitDotsGhost = limitDotsGhost.merge(limitDotsGhostEnter)

    limitDotsGhost.attr('fill', 'transparent').transition().duration(800).attr('opacity', 1)

    let limitDots = breakpoints
      .merge(breakpointsEnter)
      .selectAll('.limit-dot')
      .data(
        d => getLimitDots(d.parentId, d.id),
        (_, i) => i
      )
    const limitDotsEnter = limitDots
      .enter()
      .append('circle')
      .attr('class', 'limit-dot')
      .attr('r', 12)
      .attr('opacity', 0)
    //.style('stroke-dasharray', 2)
    limitDots.exit().transition().duration(800).attr('r', 0).attr('opacity', 0).remove()
    limitDots = limitDots.merge(limitDotsEnter)

    limitDots
      .attr('fill', 'transparent')
      .attr('stroke', d => this.color({ id: d.parentId }, 0))
      .transition()
      .duration(800)
      .attr('opacity', 1)

    // LINES DATUMS LABELS
    let lineDatumValues = breakpointsEnter
      .merge(breakpoints)
      .selectAll('.line-datum-value')
      .data(d => (this.config.showLineDatumValues ? [d] : []))
    lineDatumValues.exit().transition().style('opacity', 0).remove()
    let linesDatumValuesEnter = lineDatumValues
      .enter()
      .append('g')
      .attr('class', 'line-datum-value')
      .style('opacity', 0)

    linesDatumValuesEnter.append('rect')

    linesDatumValuesEnter.append('text').style('font-weight', 'bold').attr('fill', 'currentColor')

    lineDatumValues = lineDatumValues.merge(linesDatumValuesEnter)

    lineDatumValues
      .attr(
        'font-size',
        this._parent.accessibilityOptions?.[ACCESSIBILITY_OPTIONS_KEYS.increaseTextSize]
          ? '18px'
          : '14px'
      )
      .transition()
      .style('opacity', 1)

    lineDatumValues.select('text').text(d => this.formatValue(d.value, 'y_0'))
    lineDatumValues.select('text').attr('text-anchor', 'middle').attr('x', 0).attr('y', -12)
    if (this.config.showLineDatumValues) {
      let bbox = lineDatumValues.select('text').node().getBBox()
      lineDatumValues
        .select('rect')
        .attr(
          'fill',
          isDarkMode(this.config, this._parent.accessibilityOptions)
            ? 'rgba(31, 31, 31, 0.8)'
            : 'rgba(255,255,255,0.8)'
        )
        .attr('width', bbox.width + 4)
        .attr('height', bbox.height)
        .attr('x', -bbox.width / 2 - 2)
        .attr('y', -12 - bbox.height / 2 - 5)
        .attr('rx', 2)
    }
  }
  getLegendData() {
    let allData = this.allData || this.data
    let legendData = {
      lines: [...allData.lines],
      bars: this.cachedColorScale[1].isSinlge
        ? [{ id: allData.bars.id, label: allData.bars.id }]
        : this.config.simple
        ? [...allData.bars]
        : [...allData.bars[0].value], //Distintas legends para multi i single lines and bars
    }
    legendData.lines.icon = 'line'
    legendData.bars.icon = 'square'
    legendData.bars.__forceDraw__ = true
    return legendData
  }
}
